import * as React from "react"
import type { DateRange } from "react-day-picker"

import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"
import {
  getMonthYearBounds,
  isMonthYearOutOfBounds,
  MONTH_OPTIONS,
  monthRangeFromKeys,
  monthYearSortKey,
  startOfMonth,
} from "./month-range.js"

type MonthGridLabels = {
  previousYear?: string
  nextYear?: string
}

type MonthGridProps = {
  value?: DateRange
  onChange: (range: DateRange | undefined) => void
  startMonth: Date
  endMonth: Date
  fromDate?: Date
  toDate?: Date
  labels?: MonthGridLabels
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function MonthGrid({
  value,
  onChange,
  startMonth,
  endMonth,
  fromDate,
  toDate,
  labels,
}: MonthGridProps) {
  const minYear = startMonth.getFullYear()
  const maxYear = endMonth.getFullYear()
  const bounds = React.useMemo(
    () => getMonthYearBounds({ fromDate, toDate }),
    [fromDate, toDate],
  )

  const [displayYear, setDisplayYear] = React.useState(() =>
    clamp(value?.from?.getFullYear() ?? Math.min(maxYear, new Date().getFullYear()), minYear, maxYear),
  )
  // Pending anchor for in-progress (drag or two-click) selection.
  const [anchorKey, setAnchorKey] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (anchorKey !== null || !value?.from) return
    setDisplayYear(clamp(value.from.getFullYear(), minYear, maxYear))
  }, [value?.from, anchorKey, minYear, maxYear])
  const [hoverKey, setHoverKey] = React.useState<number | null>(null)
  const draggingRef = React.useRef(false)
  const movedRef = React.useRef(false)

  React.useEffect(() => {
    const stopDragging = () => {
      draggingRef.current = false
    }
    window.addEventListener("pointerup", stopDragging)
    return () => window.removeEventListener("pointerup", stopDragging)
  }, [])

  const committedFromKey = value?.from
    ? monthYearSortKey(value.from.getMonth(), value.from.getFullYear())
    : null
  const committedToKey = value?.to
    ? monthYearSortKey(value.to.getMonth(), value.to.getFullYear())
    : null

  let lowKey: number | null
  let highKey: number | null
  if (anchorKey !== null && hoverKey !== null) {
    lowKey = Math.min(anchorKey, hoverKey)
    highKey = Math.max(anchorKey, hoverKey)
  } else if (anchorKey !== null) {
    lowKey = anchorKey
    highKey = anchorKey
  } else {
    lowKey = committedFromKey
    highKey = committedToKey
  }

  function startSelection(key: number) {
    draggingRef.current = true
    movedRef.current = false
    const beginningNew = anchorKey === null
    if (beginningNew) {
      setAnchorKey(key)
      setHoverKey(key)
      // Emit a pending from-only range so summaries update; not yet confirmable.
      const { from } = monthRangeFromKeys(key, key)
      onChange({ from: startOfMonth(from!) })
      return
    }
    // Second click of a two-click selection finalizes the range.
    onChange(monthRangeFromKeys(anchorKey, key))
    setAnchorKey(null)
    setHoverKey(null)
  }

  function extendSelection(key: number) {
    if (!draggingRef.current || anchorKey === null) return
    if (key !== anchorKey) movedRef.current = true
    setHoverKey(key)
    onChange(monthRangeFromKeys(anchorKey, key))
  }

  function endSelection(key: number) {
    if (anchorKey === null) return
    if (movedRef.current && key !== anchorKey) {
      onChange(monthRangeFromKeys(anchorKey, key))
      setAnchorKey(null)
      setHoverKey(null)
    }
    // No movement → keep the anchor pending for a follow-up click (two-click mode).
  }

  const canGoPrev = displayYear > minYear
  const canGoNext = displayYear < maxYear

  return (
    <div className="flex w-full flex-col gap-2 select-none">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={labels?.previousYear ?? "Previous year"}
          disabled={!canGoPrev}
          onClick={() => setDisplayYear((year) => clamp(year - 1, minYear, maxYear))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-input-border bg-background text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
        >
          <Icon name="chevron-left" size="sm" />
        </button>
        <span className="text-sm font-medium text-foreground">{displayYear}</span>
        <button
          type="button"
          aria-label={labels?.nextYear ?? "Next year"}
          disabled={!canGoNext}
          onClick={() => setDisplayYear((year) => clamp(year + 1, minYear, maxYear))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-input-border bg-background text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
        >
          <Icon name="chevron-right" size="sm" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {MONTH_OPTIONS.map((option) => {
          const key = monthYearSortKey(option.value, displayYear)
          const disabled = isMonthYearOutOfBounds(option.value, displayYear, bounds)
          const inRange = lowKey !== null && highKey !== null && key >= lowKey && key <= highKey
          const isEndpoint = key === lowKey || key === highKey

          return (
            <button
              key={option.value}
              type="button"
              role="gridcell"
              aria-selected={inRange}
              aria-label={`${option.label} ${displayYear}`}
              disabled={disabled}
              onPointerDown={() => startSelection(key)}
              onPointerEnter={() => extendSelection(key)}
              onPointerUp={() => endSelection(key)}
              className={cn(
                "inline-flex h-9 items-center justify-center rounded-sm text-xs font-medium outline-none transition-[color,background-color,transform] duration-150 ease-out",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                "active:scale-95 disabled:pointer-events-none disabled:text-muted-foreground/30",
                inRange && !isEndpoint && "bg-primary-subtle text-primary-subtle-foreground",
                isEndpoint && inRange
                  ? "bg-primary text-primary-foreground hover:bg-primary-hover"
                  : !inRange &&
                      "text-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export { MonthGrid, type MonthGridLabels }
